/**
 * Turning a document path into something Mongo can address.
 *
 * A persisted path becomes a Mongo FIELD NAME, and field names may not contain
 * a dot or begin with `$`. Parts of a playEngine config are keyed by strings the
 * user wrote — `globalVariableOverrides[lang]` is keyed by dotted variable paths
 * like "hand.scale.portrait" — so a path cannot be assumed addressable just
 * because lodash can walk it.
 *
 * The rule here is derived rather than enumerated: walk the path and stop at the
 * deepest ancestor that is still legal, then write that whole value. A map that
 * is stored as JSON text (see the server's playEngineConfigMongoCodec) stops the
 * walk at itself, because Mongo cannot descend into a string. The next
 * user-keyed map added to the config is therefore safe on the day it lands,
 * without anyone remembering to add it to a list.
 */

import { toSegments } from "./setIn";

/** Roots versionPlayEngine/updateOne accepts as dotted `$set` paths. */
const PERSISTABLE_ROOTS = ["playEngineConfig", "dashboardMeta"];

/**
 * Fields whose stored form is JSON text. Must match OPAQUE_UPDATE_PATHS in
 * server/model/playEngineConfigMongoCodec — the server refuses anything below
 * one, so a client that walked deeper would only produce rejected saves.
 */
const OPAQUE_PATHS = ["playEngineConfig.localization.globalVariableOverrides"];

const FORBIDDEN_SEGMENTS = new Set(["__proto__", "constructor", "prototype"]);

function isIndexSegment(segment) {
  return /^(0|[1-9]\d*)$/.test(segment);
}

/** Array indices are always legal; object keys have to earn it. */
function isAddressableSegment(segment) {
  if (isIndexSegment(segment)) return true;
  if (segment === "") return false;
  if (segment.includes(".") || segment.includes("\0")) return false;
  if (segment.startsWith("$")) return false;
  if (segment.includes("[") || segment.includes("]")) return false;
  return !FORBIDDEN_SEGMENTS.has(segment);
}

/**
 * The deepest ancestor of `path` that Mongo can address.
 *
 * @param {string|Array} path
 * @returns {string} dotted, "" when not even the first segment is addressable
 */
export function safePersistPath(path) {
  const segments = toSegments(path);
  const safe = [];
  for (const segment of segments) {
    if (!isAddressableSegment(segment)) break;
    safe.push(segment);
    // Stop AT an opaque field, never inside it: its value is written whole and
    // the server encodes it on the way in.
    if (OPAQUE_PATHS.includes(safe.join("."))) break;
  }
  return safe.join(".");
}

/**
 * True when the route will accept this path as a dotted `$set` key.
 *
 * @param {string} path already passed through safePersistPath
 * @returns {boolean}
 */
export function isPersistableFieldPath(path) {
  if (!path) return false;
  return PERSISTABLE_ROOTS.some((root) => path === root || path.startsWith(`${root}.`));
}

export const PERSIST_PATH_INTERNALS = { PERSISTABLE_ROOTS, OPAQUE_PATHS };
